/**
 * Geração da capa do documento em LaTeX (modelo ArcelorMittal / AMT).
 *
 * Layout baseado no arquivo `capa.tex` fornecido: coordenadas absolutas em cm
 * a partir do canto inferior esquerdo da página A4. O desenho é injetado no
 * fundo da página via `\AddToShipoutPictureBG` (single-pass, sem
 * `remember picture`), mantendo o mecanismo já usado em produção.
 */

import { DadosApreciacao, Risco } from '../core/latex-types';
import { escapeLatex } from '../core/latex-utils';
import { IMAGENS_ESTATICAS } from '../static/latex-static-assets';

/**
 * Obtém o nome completo do elaborador ou aprovador.
 */
function obterNomeUsuario(usuario: any, externo: any): string {
  if (usuario?.nome) return usuario.nome;
  if (externo?.nome) return externo.nome;
  return '';
}

/**
 * Obtém o CREA do elaborador ou aprovador.
 */
function obterCreaUsuario(usuario: any, externo: any): string {
  if (usuario?.crea) return usuario.crea;
  if (externo?.crea) return externo.crea;
  return '';
}

/**
 * Quebra o nome completo em duas linhas, cortando no espaço mais próximo do
 * meio (medido em caracteres). Cada metade é escapada para LaTeX e unida por
 * `\\` (quebra de linha do TikZ). Nomes com uma única palavra ficam em uma
 * linha só. Mantém os nomes dentro das células estreitas de ELAB./VERIF./APROV.
 */
function quebrarNomeMeio(nomeCompleto: string): string {
  const partes = String(nomeCompleto || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (partes.length < 2) return escapeLatex(nomeCompleto || '');

  const total = partes.reduce((soma, p) => soma + p.length, 0) + (partes.length - 1);
  const meio = total / 2;

  let melhorCorte = 1;
  let menorDistancia = Infinity;
  let acumulado = 0;
  for (let i = 0; i < partes.length - 1; i++) {
    acumulado += partes[i].length + 1; // inclui o espaço entre as palavras
    const distancia = Math.abs(acumulado - meio);
    if (distancia < menorDistancia) {
      menorDistancia = distancia;
      melhorCorte = i + 1;
    }
  }

  const linha1 = escapeLatex(partes.slice(0, melhorCorte).join(' '));
  const linha2 = escapeLatex(partes.slice(melhorCorte).join(' '));
  return `${linha1}\\\\${linha2}`;
}

/** Linhas de revisão que o desenho da capa comporta (y 26.80→26.40→25.90). */
const LINHAS_REVISAO_NA_CAPA = 2;

/**
 * Centro vertical de cada linha de revisão. As faixas vão de 26.80→26.40 e
 * 26.40→25.90; a primeira é a mais baixa das três (0.40 cm), então o centro
 * dela fica 0.03 abaixo do geométrico para o nome em duas linhas não encostar
 * na divisória de cima.
 */
const Y_LINHAS_REVISAO = [26.62, 26.15];

export function gerarCapaLatex(dados: DadosApreciacao, _riscos: Risco[] = []): string {
  // Todos os campos vêm do frontend (formulário / máquina vinculada / usuários
  // atribuídos). Quando não preenchidos, ficam em branco — sem valores de exemplo.
  const numeroArcelorMittal = dados.numeroArcelorMittal || '';
  const numeroEmitente = dados.numeroEmitente || dados.numero || '';
  const emitente = dados.emitente || '';
  const revisao = dados.revisao ?? 0;

  // Elaborador / verificador / aprovador (nomes e CREA vindos do frontend)
  const elaboracaoCompleto = obterNomeUsuario(dados.elaborador, dados.elaboradorExterno);
  const elaboracaoCrea = obterCreaUsuario(dados.elaborador, dados.elaboradorExterno);
  const verificacaoCompleto = obterNomeUsuario(dados.verificador, dados.verificadorExterno);
  const verificacaoCrea = obterCreaUsuario(dados.verificador, dados.verificadorExterno);
  // APROV. usa o "Info Responsável Técnico" (com fallback para o campo aprovador).
  const aprovacaoCompleto = obterNomeUsuario(
    dados.responsavelTecnico || dados.aprovador,
    dados.responsavelTecnicoExterno || dados.aprovadorExterno,
  );
  const aprovacaoCrea = obterCreaUsuario(
    dados.responsavelTecnico || dados.aprovador,
    dados.responsavelTecnicoExterno || dados.aprovadorExterno,
  );

  const crea = dados.emitirCREA || elaboracaoCrea || verificacaoCrea || aprovacaoCrea || '';

  /** Formata data em pt-BR; aceita YYYY-MM-DD ou ISO (extrai só a parte da data para evitar viés de timezone). */
  const formatarDataPtBr = (valor: any): string => {
    if (valor == null || String(valor).trim() === '') return '';
    const raw = String(valor).trim();
    const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
      const [ano, mes, dia] = match[1].split('-');
      return `${dia}/${mes}/${ano}`;
    }
    const d = new Date(valor);
    if (!isNaN(d.getTime())) {
      const dia = String(d.getDate()).padStart(2, '0');
      const mes = String(d.getMonth() + 1).padStart(2, '0');
      const ano = d.getFullYear();
      return `${dia}/${mes}/${ano}`;
    }
    return raw.substring(0, 10);
  };

  const dataEmissao = formatarDataPtBr(dados.dataEmissao);

  // A tabela REFERÊNCIA/REVISÃO tem a linha da emissão inicial e mais DUAS
  // linhas desenhadas para revisões. Havendo mais que isso, as duas últimas são
  // as que aparecem — é o que o formulário da AMT comporta.
  const revisoesDoDocumento = [...(dados.revisoes ?? [])]
    .filter((r) => Number.isFinite(r?.numeroRevisao))
    .sort((a, b) => a.numeroRevisao - b.numeroRevisao);
  const linhasRevisao = revisoesDoDocumento.slice(-LINHAS_REVISAO_NA_CAPA);
  const temRevisao = linhasRevisao.length > 0;

  // Sem revisão registrada, a linha da emissão inicial continua imprimindo o
  // campo `revisao` da apreciação — é como as capas já emitidas saíram, e há
  // documento antigo com esse número preenchido sem ciclo nenhum no sistema.
  const numeroLinhaInicial = temRevisao ? 0 : revisao;

  // Nomes quebrados em duas linhas no meio (fonte menor nas células de nome).
  const elaboracao = quebrarNomeMeio(elaboracaoCompleto);
  const verificacao = quebrarNomeMeio(verificacaoCompleto);
  const aprovacao = quebrarNomeMeio(aprovacaoCompleto);

  // Número de folhas (total de páginas do documento).
  const numeroFolhas = '\\pageref{LastPage}';

  // ÁREA é fixa na capa, independente da máquina vinculada.
  const area = 'DOCUMENTOS GERAIS DE NORMAS REGULAMENTADORAS';
  const demanda = dados.demanda || dados.numeroDocumento || dados.codigoInterno || '';

  // Bloco TÍTULO: título/descrição do documento, área (C.2.3) e nº Sismana (linhas separadas).
  const titulo = (dados.titulo && String(dados.titulo).trim()) || '';
  const areaMaquina = dados.maquina?.area || '';
  // Por enquanto: usar o código Praxis no lugar do nº Sismana nesta linha.
  const numeroSismana = dados.ativo?.numeroAprPraxis || '';
  const tituloBloco = [titulo, areaMaquina, numeroSismana]
    .filter((v) => v && String(v).trim())
    .map((v) => escapeLatex(String(v)))
    .join('\\\\');

  return `

% Redefinir tamanhos de fonte personalizados
\\renewcommand{\\tiny}{\\fontsize{6pt}{7.2pt}\\selectfont}
\\newcommand{\\xtiny}{\\fontsize{5pt}{6pt}\\selectfont}
\\newcommand{\\xxtiny}{\\fontsize{4pt}{4.8pt}\\selectfont}
\\renewcommand{\\small}{\\fontsize{7pt}{8.4pt}\\selectfont}
\\newcommand{\\medium}{\\fontsize{8pt}{9.6pt}\\selectfont}
\\renewcommand{\\large}{\\fontsize{9pt}{10.8pt}\\selectfont}
\\newcommand{\\xl}{\\fontsize{10pt}{12pt}\\selectfont}
\\newcommand{\\xxl}{\\fontsize{11pt}{13.2pt}\\selectfont}
\\newcommand{\\xxxl}{\\fontsize{12pt}{14.4pt}\\selectfont}
\\newcommand{\\xxxxl}{\\fontsize{13pt}{15.6pt}\\selectfont}
\\newcommand{\\gl}{\\fontsize{14pt}{16.8pt}\\selectfont}

% Fonte serifada para o nome do EMITENTE (identificacao inferior).
% "Times New Roman" nao existe no Linux; usa-se Liberation Serif, metricamente
% compativel (fonts-liberation). No Windows pode-se trocar por "Times New Roman".
\\newfontfamily\\emitenteTimes{Liberation Serif}[Ligatures=TeX]


\\thispagestyle{empty}

\\AddToShipoutPictureBG*{%
\\AtPageLowerLeft{%
\\begin{tikzpicture}[x=1cm,y=1cm]
  % Forçar bounding box a cobrir a página A4 inteira (21cm x 29.7cm)
  % para que o eso-pic posicione corretamente a partir de (0,0)
  \\useasboundingbox (0,0) rectangle (21,29.7);

  % Margens e área útil
  \\def\\xL{3.1}
  \\def\\xR{20.45}
  \\def\\yTop{28.2}
  \\def\\yBot{3.5}

  % Cabeçalho do rodapé (Nº ArcelorMittal)
  \\node[anchor=north west,font=\\medium] at (11.70,2.40) {N\\textsuperscript{o} ArcelorMittal};

  % ==========================================
  % CABEÇALHO (HEADER)
  % ==========================================
  \\def\\yHeadBot{24.98}

  % Definição das colunas
  \\def\\xHA{\\xL+2.55}  % Nº ArcelorMittal
  \\def\\xHB{\\xHA+0.8}  % Nº -> DIVISÃO REFERÊNCIA / REVISÃO
  \\def\\xHC{\\xHB+1.9}  % Emitente
  \\def\\xHD{\\xHC+3.85} % Descrição (bem larga)
  \\def\\xHE{\\xHD+2.45} % Elab
  \\def\\xHF{\\xHE+1.8}  % Aprov
  \\def\\xHG{\\xHF+2.45} % Crea

  % Moldura externa (borda da pagina)
  \\draw[line width=.6pt] (0.60,0.70) rectangle (20.40,28.80);
  % Moldura interna
  \\draw[line width=1pt] (2.80,1.30) rectangle (20.10,28.40);
  % Linha Horizontal q divide No de fls e EMITENTE
  \\draw[line width=2pt] (2.80,4.40) -- (7.80,4.40);
  % Linha Horizontal sob os titulos REFERENCIA/REVISAO
  \\draw[line width=.6pt] (2.80,27.80) -- (20.10,27.80);
  % Linha Horizontal sob os rotulos das colunas (topo da Rev. 0)
  \\draw[line width=.6pt] (2.80,27.40) -- (20.10,27.40);

  % Linha Horizontal q divide Rev. 0 e Rev. 1
  \\draw[line width=.6pt] (2.80,26.8) -- (20.10,26.80);
  % Linha Horizontal q divide Rev. 1 e Rev. 2
  \\draw[line width=.6pt] (2.80,26.40) -- (20.10,26.40);
  % Linha Horizontal inferior do cabecalho (borda)
  \\draw[line width=2pt] (2.80,25.90) -- (20.10,25.90);
  % Linha Vertical q divide REFERENCIA (No ArcelorMittal) e REVISAO
  \\draw[line width=2pt] (5.20,28.40) -- (5.20,25.90);
  % Linha Vertical q divide No e EMITENTE
  \\draw[line width=0.6pt] (6,27.80) -- (6,25.90);
  % Linha Vertical q divide EMITENTE e DESCRICAO
  \\draw[line width=.6pt] (8,27.80) -- (8,25.90);
  % Linha Vertical q divide DESCRICAO e ELAB.
  \\draw[line width=.6pt] (12.70,27.80) -- (12.70,25.90);

  % Títulos Superiores
  % Linha Vertical q divide ELAB. e APROV.
  \\draw[line width=.6pt] (14.30,27.80) -- (14.30,25.90);
  % Linha Vertical q divide APROV. e RN
  \\draw[line width=.6pt] (16.10,27.80) -- (16.10,25.90);

  % Títulos das Colunas
  % Linha Vertical q divide RN e DATA
  \\draw[line width=.6pt] (18.70,27.80) -- (18.70,25.90);
  \\node[anchor=center,font=\\medium] at (4,28.10) {REFER\\'ENCIA};
  \\node[anchor=center,font=\\medium] at (12.50,28.10) {REVIS\\~AO};
  \\node[font=\\medium,align=center] at (4,27.60) {N\\textsuperscript{o} ArcelorMittal};
  \\node[font=\\medium,align=center] at (5.60,27.60) {N\\textsuperscript{o}};
  \\node[font=\\medium,align=center] at (7.10,27.60) {EMITENTE};
  \\node[font=\\medium,align=center] at (10.30,27.60) {DESCRI\\c{C}\\~AO};
  \\node[font=\\medium,align=center] at (13.40,27.60) {ELAB.};

  % Dados da Primeira Linha (Revisão 0)
  \\def\\yRowOne{\\yTop-1.6}
  \\node[font=\\medium,align=center] at (15.20,27.60) {APROV.};
  \\node[font=\\medium,align=center] at (17.40,27.60) {REG.NAC.};
  \\node[font=\\medium,align=center] at (19.40,27.60) {DATA};
  \\node[font=\\small,align=center] at ({(\\xHA+\\xHB)/2 - 0.5},27.20) {${numeroLinhaInicial}};
  \\node[font=\\small,align=center] at (7.05,27.15) {${escapeLatex(emitente)}};
  \\node[font=\\small,align=center] at ({(\\xHC+\\xHD)/2},27.15) {EMISS\\~AO INICIAL};
  \\node[font=\\xtiny,align=center,inner sep=0pt] at ({(\\xHD+\\xHE)/2 + 0.15},27.05) {${elaboracao}};

  % Linhas de revisão. A coluna Nº ArcelorMittal (x=4) fica vazia de propósito:
  % o número AMT vive no rodapé, não se repete aqui.
${linhasRevisao
  .map((r, i) => {
    const y = Y_LINHAS_REVISAO[i];
    const dataRevisao = formatarDataPtBr(r.concluidaEm);
    return [
      `  \\node[font=\\small,align=center] at ({(\\xHA+\\xHB)/2 - 0.5},${y}) {${r.numeroRevisao}};`,
      `  \\node[font=\\small,align=center] at (7.05,${y}) {${escapeLatex(emitente)}};`,
      `  \\node[font=\\xxtiny,align=center,inner sep=0pt] at ({(\\xHC+\\xHD)/2},${y}) {${escapeLatex(r.descricaoCapa || 'REVISÃO')}};`,
      // As faixas de revisão são mais baixas que a da emissão inicial (0.40 e
      // 0.50 cm contra 0.60): com \\xtiny o nome em duas linhas encosta na
      // divisória de cima.
      `  \\node[font=\\xxtiny,align=center,inner sep=0pt] at ({(\\xHD+\\xHE)/2 + 0.15},${y}) {${elaboracao}};`,
      `  \\node[font=\\xxtiny,align=center,inner sep=0pt] at (15.20,${y}) {${aprovacao}};`,
      `  \\node[font=\\small,align=center] at (17.40,${y}) {${escapeLatex(crea)}};`,
      dataRevisao ? `  \\node[font=\\small,align=center] at (19.40,${y}) {${escapeLatex(dataRevisao)}};` : '',
    ]
      .filter(Boolean)
      .join('\n');
  })
  .join('\n')}

  % ========================= Status =========================
  \\def\\yStatusTop{12.65}
  \\def\\yStatusMidA{11.15}
  \\def\\yStatusMidB{10.45}
  \\def\\yStatusMidC{9.65}
  \\def\\xStatusLeft{\\xL+3.55}  % Divisão entre esquerda e checkboxes
  \\def\\xStatusRight{\\xR-3.68} % Divisão antes do bloco direito

  \\node[font=\\xtiny,align=center,inner sep=0pt] at (15.20,27.05) {${aprovacao}};
  \\node[font=\\small,align=center] at (17.40,27.05) {${escapeLatex(crea)}};
  \\node[font=\\small,align=center] at (19.40,27.20) {${escapeLatex(dataEmissao)}};
  % Linha Horizontal superior do bloco de liberacao (borda)
  \\draw[line width=2pt] (2.80,10.50) -- (20.10,10.40);

  % Linhas verticais
  % Linha Horizontal interna do bloco de liberacao
  \\draw[line width=.6pt] (2.80,8.90) -- (16.40,8.90);
  % Linha Horizontal interna do bloco de liberacao
  \\draw[line width=.6pt] (6.30,8.20) -- (16.40,8.20);

  % Primeira linha esquerda
  % Linha Horizontal inferior do bloco de liberacao (borda)
  \\draw[line width=2pt] (2.80,7.45) -- (20.10,7.45);
  % Linha Vertical q divide o bloco de cancelamento e os checkboxes
  \\draw[line width=2pt] (6.30,10.50) -- (6.30,7.42);

  % Checkboxes - primeira linha
  % Linha Vertical q divide os checkboxes e o bloco Centro de Documentacao
  \\draw[line width=2pt] (16.40,10.40) -- (16.40,7.48);
  \\node[anchor=west,font=\\tiny] at (2.80,10.20) {CANCELA E SUBSTITUI};

  % Checkboxes - segunda linha (ainda na primeira faixa)
  \\node[anchor=west,font=\\tiny] at (2.80,10) {O DOCUMENTO NUMERO:};
  \\node[anchor=west,font=\\tiny\\bfseries] at (6.50,9.85) {LIBERADO};

  % Bloco direito - primeira linha
  \\node[anchor=center,font=\\tiny\\bfseries] at (13.90,9.90) {LIBERADO C/ RESSALVAS};
  \\node[anchor=west,font=\\tiny\\bfseries] at (6.50,9.30) {N\\~AO LIBERADO};
  \\node[anchor=center,font=\\tiny\\bfseries] at (13,9.30) {VISTO};

  % Quadrados de status (checkboxes) — alinhados 2 a 2 (grid de 2 colunas).
  % Coluna esquerda (x 8.30–8.55): após LIBERADO (cima) e NÃO LIBERADO (baixo).
  % Coluna direita  (x 15.50–15.75): após LIBERADO C/ RESSALVAS (cima) e VISTO (baixo).
  % Lado do quadrado = 0.25cm; traço = 0.6pt.
  \\draw[line width=.6pt] (8.40,9.725) rectangle (8.65,9.975);   % após LIBERADO
  \\draw[line width=.6pt] (8.40,9.175) rectangle (8.65,9.425);   % após N\\~AO LIBERADO
  \\draw[line width=.6pt] (15.50,9.725) rectangle (15.75,9.975); % após LIBERADO C/ RESSALVAS
  \\draw[line width=.6pt] (15.50,9.175) rectangle (15.75,9.425); % após VISTO

  % Segunda linha esquerda
  \\node[anchor=west,font=\\tiny\\bfseries] at (16.60,10.10) {CENTRO DE DOCUMENTA\\c{C}\\~AO};
  \\node[anchor=west,font=\\tiny\\bfseries] at (16.60,9.80) {E INFORMA\\c{C}\\~OES T\\'ECNICAS};

  % Campos NOME, ASS., etc - segunda linha meio
  \\def\\xNomeA{\\xStatusLeft+2}
  \\def\\xNomeB{\\xStatusLeft+3.8}
  \\def\\xNomeC{\\xStatusLeft+5.5}
  \\def\\xNomeD{\\xStatusLeft+8.8}

  \\node[anchor=west,font=\\tiny\\bfseries] at (16.60,9) {DATA \\underline{\\hspace{0.8cm}}/\\underline{\\hspace{0.8cm}}/\\underline{\\hspace{0.8cm}}};
  \\node[anchor=west,font=\\tiny] at (2.80,8.60) {CANCELADO E SUBSTITUIDO};
  \\node[anchor=west,font=\\tiny] at (2.80,8.35) {PELO DOCUMENTO NUMERO:};
  % Linha Vertical q divide ASS. e ORGAO
  \\draw[line width=.6pt] (8.50,8.90) -- (8.50,8.20);

  % Linha Vertical q divide ORGAO e IEMM
  \\draw[line width=.6pt] (10.10,8.90) -- (10.10,8.20);
  % Linha Vertical q divide IEMM e CREA
  \\draw[line width=.6pt] (11.80,8.90) -- (11.80,8.20);
  % Linha Vertical q divide CREA e DATA
  \\draw[line width=.6pt] (15.10,8.90) -- (15.10,8.20);
  \\node[anchor=west,font=\\tiny\\bfseries] at (7,8.70) {NOME};
  \\node[anchor=west,font=\\tiny\\bfseries] at (8.90,8.70) {ASS.};

  % Bloco direito - segunda linha
  \\node[anchor=west,font=\\tiny\\bfseries] at (10.50,8.70) {ORG\\~AO};

  % Terceira linha
  \\node[anchor=west,font=\\tiny\\bfseries] at (12.10,8.70) {CREA};
  \\node[anchor=west,font=\\tiny\\bfseries] at (15.40,8.70) {DATA};

  % Bloco direito - terceira linha
  \\node[anchor=west,font=\\tiny\\bfseries] at (16.60,8.30) {N\\textsuperscript{o} DOC. };

  % ========================= Emissão / Identificação =========================
  \\def\\yEmissaoTop{9.650}
  \\def\\yEmissaoBot{6.55}
  \\def\\xDivMain{\\xL+5.05}

  \\node[anchor=west,font=\\tiny\\bfseries] at (6.60,8)
    {ESTA LIBERA\\c{C}\\~AO N\\~AO ISENTA A CONTRATADA DE SUAS RESPONSABILIDADES};

  % Lado Esquerdo: EMISSÃO
  \\node[anchor=west,font=\\tiny\\bfseries] at (6.60,7.70) {CONTRATUAIS};
  \\node[anchor=west,font=\\tiny\\bfseries] at (16.70,7.70) {ASS. \\underline{\\hspace{2.70cm}}};
  % Linha Vertical q divide EMISSAO (esq.) e IDENTIFICACAO (dir.)
  \\draw[line width=2pt] (7.80,2.40) -- (7.80,7.42);
  \\node[anchor=west,font=\\medium] at (4.60,7.10) {EMISS\\~AO};
  % Linha Horizontal q divide VERIF. e APROV.
  \\draw[line width=.6pt] (2.80,5.90) -- (7.80,5.90);
  % Linha Horizontal q divide ELAB. e VERIF.
  \\draw[line width=.6pt] (2.80,6.40) -- (7.80,6.40);
  % Linha Horizontal q divide aprov e RN
  \\draw[line width=.6pt] (4.2,5.40) -- (7.80,5.40);

  \\def\\xEA{\\xL+1.58}
  \\def\\xEB{\\xL+3.5}
  % Linha Horizontal q divide EMISSAO (titulo) e ELAB.
  \\draw[line width=.6pt] (2.80,6.90) -- (7.80,6.90);
  % Linha Horizontal q divide RN e No de fls
  \\draw[line width=.6pt] (2.80,4.90) -- (7.80,4.90);

  % Linha Vertical q divide os rotulos e os valores
  \\draw[line width=.6pt] (4.20,6.90) -- (4.20,4.40);
  % Linha Vertical q divide os valores e as datas (ELAB./VERIF./APROV.)
  \\draw[line width=.6pt] (6.20,6.90) -- (6.20,5.40);
  % Cada linha (ELAB./VERIF./APROV./CREA/Nº de fls) recebe o valor do frontend
  % alinhado à sua faixa. Coluna de valor centrada em x=5.20; data em x=7.0.
  \\node[anchor=west,font=\\medium] at (2.8,6.60) {ELAB.};
  \\node[anchor=center,align=center,inner sep=0pt,font=\\xtiny] at (5.20,6.65) {${elaboracao}};
  \\node[anchor=center,font=\\small] at (7.0,6.65) {${escapeLatex(dataEmissao)}};

  \\node[anchor=west,font=\\medium] at (2.8,6.10) {VERIF.};
  \\node[anchor=center,align=center,inner sep=0pt,font=\\xtiny] at (5.20,6.15) {${verificacao}};
  \\node[anchor=center,font=\\small] at (7.0,6.15) {${escapeLatex(dataEmissao)}};

  \\node[anchor=west,font=\\medium] at (2.8,5.60) {APROV.};
  \\node[anchor=center,align=center,inner sep=0pt,font=\\xtiny] at (5.20,5.65) {${aprovacao}};
  \\node[anchor=center,font=\\small] at (7.0,5.65) {${escapeLatex(dataEmissao)}};

  \\node[anchor=west,font=\\medium] at (2.75,5.15) {REG.NAC.};
  \\node[anchor=center,font=\\small] at (6,5.15) {${escapeLatex(crea)}};

  \\node[anchor=west,font=\\medium] at (2.8,4.70) {N\\textsuperscript{o} de fls};
  \\node[anchor=center,font=\\small] at (6,4.65) {${numeroFolhas}};

  % Lado Direito: IDENTIFICAÇÃO
  \\node[anchor=west,font=\\medium] at (3,4.20) {EMITENTE};

  \\node[anchor=center,font=\\emitenteTimes\\fontsize{22pt}{26.4pt}\\selectfont] at (5.30,3.20) {${escapeLatex(emitente)}};

  \\node[anchor=west,font=\\bfseries\\gl] at (8,6.65) {ArcelorMittal\\hspace{0.4cm}Tubar\\~ao};

  \\node[anchor=east] at (19.60,6.90) {\\includegraphics[height=0.65cm]{${IMAGENS_ESTATICAS.ARCELOR_LOGO}}};
  % Linha Horizontal q divide ArcelorMittal Tubarao e AREA
  \\draw[line width=2pt] (7.80,6.40) -- (20.10,6.40);

  \\node[anchor=west,font=\\small] at (8,6.20) {\\'AREA};

  \\node[anchor=west,font=\\xxl] at (8,5.70) {${escapeLatex(area)}};

  % Linha Horizontal q divide AREA e TITULO
  \\draw[line width=2pt] (7.80,5.40) -- (20.10,5.40);
  % Linha Vertical q divide ArcelorMittal Tubarao e o logo
  \\draw[line width=2pt] (17.60,7.48) -- (17.60,6.40);

  % --- Faixa Final (Rodapé Inferior) ---
  \\def\\yRodapeTop{4.6}
  \\def\\yRodapeBot{3.5}

  % Divisões verticais
  \\def\\xRone{\\xL+5.05}   % Fim do Nº Emitente
  \\def\\xRtwo{\\xRone+3.3}  % Fim da Demanda
  \\def\\xRthree{\\xR-1.75}  % Fim do Nº ArcelorMittal

  \\node[anchor=west,font=\\small] at (8.10,5.20) {T\\'ITULO};
  \\node[anchor=north west,font=\\xxxl,align=left] at (8,4.80) {${tituloBloco}};
  % Linha Horizontal superior do rodape
  \\draw[line width=.6pt] (2.80,2.40) -- (20.10,2.30);
  % Linha Vertical q divide No EMITENTE e DEMANDA
  \\draw[line width=2pt] (7.80,1.30) -- (7.80,2.40);

  % 1. Nº Emitente
  % Linha Vertical q divide DEMANDA e No ArcelorMittal
  \\draw[line width=2pt] (11.60,1.30) -- (11.60,2.36);
  % Linha Vertical q divide No ArcelorMittal e REVISAO
  \\draw[line width=.6pt] (18.40,1.30) -- (18.40,2.30);

  % 2. Demanda
  \\node[anchor=north west,font=\\medium] at (2.90,2.40) {N\\textsuperscript{o} EMITENTE};
  \\node[anchor=center,font=\\xxl] at (5.20,1.80) {${escapeLatex(numeroEmitente)}};

  % 3. Nº ArcelorMittal (com fundo amarelo)
  \\node[anchor=north west,font=\\medium] at (7.90,2.40) {DEMANDA};
  \\node[anchor=center,font=\\xxl] at (9.60,1.80) {${escapeLatex(demanda)}};

  % 4. Revisão
  \\node[anchor=east,font=\\xxl] at (15.70,1.80) {${escapeLatex(numeroArcelorMittal)}};
  \\node[anchor=north,font=\\medium] at (19.30,2.40) {REVIS\\~AO};

  % Texto lateral
  \\node[anchor=center,font=\\xxl] at (19.20,1.80) {${revisao}};
  
  \\node[anchor=center,font=\\medium] at (19.60,1.1) {A4};

  \\node[anchor=south] at (1.30,1.20)
  {\\includegraphics[height=6cm]{${IMAGENS_ESTATICAS.ARCELOR_LATERAL}}};
\\end{tikzpicture}%
}%
}%
\\null% Garante que a página da capa existe para o shipout

\\newpage
`;
}
